import React, { ChangeEvent, FC, useMemo, useRef, useState } from 'react';
import { Item } from 'react-photoswipe-gallery';
import 'photoswipe/dist/photoswipe.css';
import { useReplaceImage } from '@/api/Inspections/hooks/useInspectionsVehicles';
import { acceptImageFiles, checkIsImage } from '@/utils/constants';
import { LightBoxItemProps } from './types';
import { faDownload, faEllipsisV, faTrash, faUpload, Icon } from '../Icon';
import { Button, Dropdown, Empty, type MenuProps } from 'antd';
import { downloadFile, getFileName } from '@/utils/helpers';
import useApp from 'antd/es/app/useApp';

export const LightBoxItem: FC<LightBoxItemProps> = ({
  image,
  className,
  style,
  showNoImage,
  actions,
}) => {
  const { modal } = useApp();
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const { mutateAsync: replaceImageAsync } = useReplaceImage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasContent = image.thumbnailImageUrl && image.thumbnailImageUrl.length;
  const isImage =
    checkIsImage(image.thumbnailImageUrl) ||
    (image.thumbnailImageUrl?.startsWith('blob:') &&
      ['image/jpeg', 'image/png', 'image/webp'].includes(
        image.contentType ?? '',
      ));
  const showReplaceImage = !!image.replaceImage && !!actions?.replaceImage;
  const showDownload = actions?.download != false;
  const showDelete = !!actions?.onDelete;

  const readFileAsBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = ({ target }) => {
        if (typeof target?.result === 'string') {
          resolve(target.result);
          return;
        }
        reject(new Error('Failed to read file'));
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const onReplaceFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !image.replaceImage) return;

    const imageFileBase64 = await readFileAsBase64(file);
    await replaceImageAsync({
      id: image.replaceImage.id,
      imageType: image.replaceImage.type,
      imageFileName: file.name,
      imageFileBase64,
    });
    actions?.replaceImage?.(image.replaceImage.id);
  };

  const slideWidth = (dimensions?.width ?? 20) * 5;
  const slideHeight = (dimensions?.height ?? 20) * 5;

  /*
   * react-photoswipe-gallery Item uses useCallback(..., [set, ...Object.values(restProps)]).
   * Always pass the same set of InternalItem keys so Object.values length stays stable (package exports only ".").
   */
  const slideProps = useMemo(
    (): Omit<React.ComponentProps<typeof Item>, 'children'> => ({
      original: image.imageFileUrl ?? '',
      thumbnail: image.thumbnailImageUrl ?? '',
      width: slideWidth,
      height: slideHeight,
      alt: image.caption || (image.id != null ? String(image.id) : ''),
      id: image.id,
    }),
    [
      image.imageFileUrl,
      image.thumbnailImageUrl,
      slideWidth,
      slideHeight,
      image.id,
      image.caption,
    ],
  );

  const menuItems = useMemo(() => {
    const items = [];
    if (showReplaceImage) {
      items.push({
        label: 'Replace Image',
        key: 'replaceImage',
        icon: <Icon icon={faUpload} />,
      });
    }
    if (showDownload) {
      items.push({
        label: 'Download',
        key: 'download',
        icon: <Icon icon={faDownload} />,
      });
    }
    if (showDelete) {
      items.push({
        label: 'Delete',
        key: 'delete',
        icon: <Icon icon={faTrash} />,
        danger: true,
      });
    }
    return items;
  }, [showReplaceImage, showDownload, showDelete]);

  const onMenuClick: NonNullable<MenuProps['onClick']> = (item) => {
    item.domEvent.stopPropagation();
    item.domEvent.preventDefault();

    if (item.key === 'replaceImage') {
      fileInputRef.current?.click();
      return;
    }
    if (item.key === 'download') {
      downloadFile(
        image.imageFileUrl,
        image.downloadFileName || getFileName(image.imageFileUrl),
      );
      return;
    }
    if (image.id && item.key === 'delete') {
      modal.confirm({
        title: 'Delete Image',
        content: 'Are you sure you want to delete this image?',
        okText: 'Delete',
        onOk: async () => actions?.onDelete?.(image.id!),
      });
    }
  };

  return (
    <Item {...slideProps}>
      {({ ref, open }) => (
        <div
          className="d-flex justify-content-center align-items-center mw-100"
          style={{ minWidth: 0, maxWidth: '100%', ...(style || {}) }}
        >
          <div className="position-relative d-inline-flex mw-100 mh-100">
            {menuItems.length > 0 ? (
              <div
                className="position-absolute"
                style={{ top: 8, right: 8, zIndex: 2 }}
                onClick={(event) => event.stopPropagation()}
              >
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  menu={{
                    items: menuItems,
                    onClick: (item) => onMenuClick(item),
                  }}
                >
                  <Button
                    shape="circle"
                    size="small"
                    icon={<Icon icon={faEllipsisV} />}
                    onClick={(event) => event.stopPropagation()}
                  />
                </Dropdown>
                {showReplaceImage ? (
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept={acceptImageFiles}
                    multiple={false}
                    onChange={onReplaceFileChange}
                    onClick={(event) => event.stopPropagation()}
                  />
                ) : null}
              </div>
            ) : null}
            {hasContent ? (
              isImage ? (
                <button
                  ref={ref}
                  onClick={open}
                  className="mw-100 mh-100 border-0 bg-transparent p-0 d-flex align-items-center justify-content-center"
                >
                  <img
                    className={`rounded ${className ? ` ${className}` : ''}`}
                    style={{
                      objectFit: 'contain',
                      maxWidth: '100%',
                      maxHeight: '100%',
                    }}
                    src={image.thumbnailImageUrl}
                    loading="lazy"
                    alt={image.caption || ''}
                    onLoad={({ currentTarget }) => {
                      setDimensions({
                        width: currentTarget.naturalWidth,
                        height: currentTarget.naturalHeight,
                      });
                    }}
                    onError={({ currentTarget }) => {
                      // prevents looping
                      currentTarget.onerror = null;
                      // reset error image
                      if (
                        image.thumbnailImageUrl &&
                        image.thumbnailImageUrl.length
                      ) {
                        currentTarget.src = 'images/image-error.png';
                        currentTarget.title = 'Failed to load image';
                      }
                    }}
                  />
                </button>
              ) : (
                <div className="h-100 d-flex align-items-center justify-content-center">
                  <Empty description="Invalid Image" />
                </div>
              )
            ) : showNoImage ? (
              <div className="d-flex flex-column align-items-center justify-content-center h-100">
                <Empty description="No Image" />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </Item>
  );
};
